import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { LoggerService } from "../../ancestors/logger.service";
import { EnvironmentLoaderService } from "../../ancestors/env-config.service";
import { AuthnApiService } from "./api/authn-api.service";
import { TokenStorageService } from "./token-storage.service";
import { TokenManagerService } from "./token-manager.service";
import { UrlCollection } from "../constants/url-collection";
import { AdminImpersonateComponent } from "../pages/admin/admin-impersonate/admin-impersonate.component";
import { AdminMonitoringDetailsComponent } from "../pages/admin/monitoring-details/monitoring-details.component";
import { NotificationStateService } from "./state/notification-state.service";

/**
 * Chiave per salvare il flag di login SSO in sessionStorage
 */
const SSO_LOGIN_KEY = "SSO_LOGIN";

/**
 * Servizio per gestire il logout completo dell'utente
 * Coordina tutte le operazioni necessarie per un logout sicuro
 */
@Injectable({ providedIn: "root" })
export class LogoutService {
  private logger = inject(LoggerService);

  private router = inject(Router);

  private envConfig = inject(EnvironmentLoaderService);

  private authnService = inject(AuthnApiService);

  private tokenStorage = inject(TokenStorageService);

  private tokenManager = inject(TokenManagerService);

  private notificationState = inject(NotificationStateService);

  constructor() {
    this.logger.setCaller(this.constructor.name);
  }

  /**
   * Effettua il logout completo dell'utente
   * - Chiama l'API di logout
   * - Rimuove il token dallo storage locale
   * - Pulisce la sessione SSO se presente
   * - Reindirizza all'Identity Provider per il logout SSO (se applicabile)
   * - Oppure reindirizza alla pagina di login locale
   * @returns Promise di completamento
   */
  async performLogout(): Promise<void> {
    this.logger.info("Starting performLogout()");

    try {
      // 1. Determina il tipo di login e la pagina di destinazione
      const env = this.envConfig.getEnvConfig();
      const tokenKey = env.loginType === "local"
        ? env.localTokenKey
        : env.ssoTokenKey;

      const isSsoLogin = sessionStorage.getItem(SSO_LOGIN_KEY);

      this.logger.info(`Login type: ${env.loginType}, SSO session: ${!!isSsoLogin}`);

      // 2. Chiama l'API di logout sul backend
      try {
        await this.authnService.logout(true);
        // disconnect SSE
        this.notificationState.disconnectSSE();
        this.logger.info("Logout API call completed");
      } catch (apiError) {
        // Se l'API fallisce, continuiamo comunque con la pulizia locale
        this.logger.warn(`Errore nella chiamata API logout, continuo con pulizia locale: ${String(apiError)}`);
      }

      // 3. Rimuove il token dallo storage locale
      this.tokenStorage.deleteToken(tokenKey);
      this.logger.info("Token removed from storage");

      // 4. Pulisce i dati di sessione locale
      this.clearSessionData();

      // 5. Pulisce i dati di sessione SSO se presenti
      if (isSsoLogin) {
        sessionStorage.removeItem(SSO_LOGIN_KEY);
        this.logger.info("SSO session data removed");
      }

      // 6. Se loginType è SSO, effettua sempre il logout sull'Identity Provider
      // Il flag isSsoLogin potrebbe non essere settato se l'utente si era loggato
      // prima che questo codice fosse deployato
      if (env.loginType === "sso") {
        await this.performSsoLogout();
      } else {
        // 7. Per login locale, reindirizza alla pagina di login locale
        this.logger.info(`Redirecting to: ${UrlCollection.localAuthentication}`);
        await this.router.navigate([UrlCollection.localAuthentication]);

        // 8. Ricarica la pagina per pulire completamente lo stato dell'applicazione
        window.location.reload();
      }
    } catch (error) {
      this.logger.error("Errore durante il logout", String(error));

      // In caso di errore, forza comunque la pulizia e il redirect
      this.forceLogout();
    }
  }

  /**
   * Effettua il logout sull'Identity Provider SSO
   * Ottiene l'URL di logout e reindirizza l'utente
   * @private
   */
  private async performSsoLogout(): Promise<void> {
    try {
      // Costruisce l'URL di redirect post-logout
      // Dopo il logout SSO, l'utente verrà reindirizzato alla pagina SSO dell'app
      // const baseUrl = window.location.origin;
      // const postLogoutRedirectUri = `${baseUrl}/#/${UrlCollection.sso}`;
      // Verrà utilizzata la pagina di logout standard dell'Identity Provider, senza riportare l'utente all'applicazione.
      const postLogoutRedirectUri: string | undefined = undefined;

      this.logger.info(`Requesting SSO logout URL with redirect to: ${postLogoutRedirectUri}`);

      // Ottiene l'URL di logout dall'Identity Provider
      const response = await this.authnService.getOAuthLogoutUrl(postLogoutRedirectUri);

      this.logger.info(`Redirecting to SSO logout URL: ${response.logoutUrl}`);

      // Reindirizza all'Identity Provider per il logout
      window.location.href = response.logoutUrl;
    } catch (error) {
      this.logger.error(`Error during SSO logout: ${String(error)}`);

      // Se il logout SSO fallisce, effettua comunque un logout locale
      this.logger.warn("SSO logout failed, falling back to local redirect");
      window.location.href = `/#/${UrlCollection.sso}`;
    }
  }

  /**
   * Pulisce tutti i dati di sessione locale
   * @private
   */
  private clearSessionData(): void {
    sessionStorage.removeItem(AdminImpersonateComponent.SESSION_KEYS.SEARCH_VALUE);
    sessionStorage.removeItem(AdminImpersonateComponent.SESSION_KEYS.PAGE);
    sessionStorage.removeItem(AdminImpersonateComponent.SESSION_KEYS.PAGE_SIZE);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SEARCHED_TEXT);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.PAGE_SIZE);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.PAGE);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_FEEDBACK_360_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_SELF_ASSESSMENT_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_EVALUATION_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_DELIVERY_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_FEEDBACK_360_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_SELF_ASSESSMENT_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_EVALUATION_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.APPLIED_DELIVERY_STATUSES);
    sessionStorage.removeItem(AdminMonitoringDetailsComponent.SESSION_KEYS.SELECTED_SORT_OPTION);
    this.logger.info("Session data cleared");
  }

  /**
   * Logout forzato in caso di errori
   * Pulisce tutto localmente e reindirizza
   * @private
   */
  private forceLogout(): void {
    this.logger.warn("Esecuzione logout forzato");

    try {
      const env = this.envConfig.getEnvConfig();
      const tokenKey = env.loginType === "local"
        ? env.localTokenKey
        : env.ssoTokenKey;

      // Rimuove tutti i token possibili
      this.tokenStorage.deleteToken(tokenKey);
      this.tokenStorage.deleteToken(env.localTokenKey);
      this.tokenStorage.deleteToken(env.ssoTokenKey);

      // Pulisce la sessione
      sessionStorage.removeItem(SSO_LOGIN_KEY);

      // Reindirizza e ricarica
      const redirectUrl = env.loginType === "sso"
        ? UrlCollection.sso
        : UrlCollection.localAuthentication;
      window.location.href = `/#/${redirectUrl}`;
    } catch (forceError) {
      this.logger.error("Errore anche nel logout forzato", String(forceError));
      // Come ultima risorsa, vai alla home e ricarica
      window.location.href = "/";
    }
  }

  /**
   * Verifica se l'utente è attualmente autenticato
   * @returns true se l'utente ha un token valido
   */
  isAuthenticated(): boolean {
    return this.tokenManager.isAuthenticated();
  }

  /**
   * Segna la sessione corrente come login SSO
   * Deve essere chiamato dopo un login SSO riuscito
   */
  markSsoLogin(): void {
    sessionStorage.setItem(SSO_LOGIN_KEY, "true");
    this.logger.info("Session marked as SSO login");
  }
}
